import React, { useState, useRef } from 'react';
import { Plus, Trash2, X, GripVertical, Sparkles } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { PresenceInput } from './ui/presence-input';
import { SortableRow, Droppable, DragHandleSpacer } from './ui/sortable';
import { getSideFromZone, findMirrorLineAllowance, flipNameSide, zoneRank } from '../lib/vehicleZones';
import PartLookupInput from './ui/part-lookup-input';
import ImageSelector from './ImageSelector';
import ApproachButton from './damage/ApproachButton';
import RemoveRefitButton from './damage/RemoveRefitButton';
import PaintButton from './damage/PaintButton';
import { dataApi } from '../services/api';
import { useQuoteModel } from '../context/QuoteModelContext';
import { clampQuantity, MAX_PART_QUANTITY } from '../lib/damageLines';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

/**
 * How many of a fitting this repair disturbs.
 *
 * The insurer guides carry ONE row for a fitting a car wears several of — one
 * "Bumper Mould" line whether the bumper has one moulding or four, one "Badges
 * Per Badge Complete" against a badge on each guard. The count is the only
 * thing that bills the rest, so it lives on the part and multiplies every
 * operation the part attracts: the strip, the paint, the beat and the part
 * itself.
 *
 * Rendered as a plain field rather than a stepper because it is almost always
 * one and nobody should have to look at it. Integers only, clamped to
 * 1..MAX_PART_QUANTITY, matching the pricing on both sides — see clampQuantity
 * in lib/damageLines.js and in the two functions it mirrors.
 */
function QuantityField({ value, onChange, disabled }) {
  const quantity = clampQuantity(value);
  return (
    <div className="flex-shrink-0">
      <input
        type="number"
        min="1"
        max={MAX_PART_QUANTITY}
        step="1"
        value={quantity}
        disabled={disabled}
        onChange={(e) => onChange(clampQuantity(e.target.value))}
        title={`How many of this the repair disturbs (1-${MAX_PART_QUANTITY}). The guide has one line for all of them, so the count is what bills the rest.`}
        aria-label="Quantity"
        className={`h-8 w-12 rounded-md border border-input bg-background px-1 text-center text-sm
          ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
          disabled:cursor-not-allowed disabled:opacity-50
          ${quantity > 1 ? 'font-medium' : 'text-muted-foreground'}`}
      />
    </div>
  );
}

const ZONES = [
  { id: 'front', label: 'Front' },
  { id: 'front-right', label: 'Front Right' },
  { id: 'right', label: 'Right' },
  { id: 'rear-right', label: 'Rear Right' },
  { id: 'rear', label: 'Rear' },
  { id: 'rear-left', label: 'Rear Left' },
  { id: 'left', label: 'Left' },
  { id: 'front-left', label: 'Front Left' },
  { id: 'roof', label: 'Roof' },
  { id: 'underside', label: 'Underside' },
  { id: 'other', label: 'Other' }
];

const DamagePartsList = ({
  selectedZones,
  onRemoveZone,
  availableImages = [],
  onCheckZoneHasParts,
  onZonesWithPartsChanged,
  onImageClick,
  filterZone = null,
  viewMode = 'sections',
  // Empty-state actions — the two ways to start an assessment, offered where the
  // list would otherwise just say there is nothing here.
  onAddSection,
  onGenerateAssessment,
  canGenerateAssessment = false,
  // Why the AI cannot be run yet, in the caller's words. Photos are one reason
  // and a missing damage description is the other, and "add photos first" is
  // actively unhelpful when the photos are already there.
  assessBlockedReason = 'Add damage photos first',
  assessmentRunning = false,
  // Field-level presence (who else is editing a part's name/description right now)
  trackFieldEdit = () => {},
  clearFieldEdit = () => {},
  getFieldEditor = () => null,
  isCurrentUserEditing = () => false,
}) => {
  // Damage state, insurer helpers and default-part resolution are owned by the
  // shared QuoteModelProvider so the quote editor and this view stay in lockstep.
  const {
    insurerConfigId,
    parts, setParts,
    damageReady,
    insurerCacheReady,
    insurerCacheRef,
    blendUnitPrice,
    getPartDefaults,
    hasInOutPaint,
    getPaintSizeOptions,
    addDefaultAccessParts,
    addDefaultBlendParts,
    addDefaultChildParts,
  } = useQuoteModel();
  const isLoading = !damageReady;
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [imageSelector, setImageSelector] = useState({ open: false, partId: null, zoneId: null, partName: '' });
  const [addPartValues, setAddPartValues] = useState({}); // Track add part input values per zone
  const [activeDragId, setActiveDragId] = useState(null); // Primary part id currently being dragged
  const dragOriginZoneRef = useRef(null); // Zone a drag started in, to detect cross-side moves
  const lastMoveDeltaRef = useRef(null); // Cursor offset the last cross-zone move was made at
  const lastZonesWithPartsRef = useRef(null); // Last zone list handed to the parent

  // Expose parts checking function to parent
  const checkIfZoneHasParts = React.useCallback((zoneId) => {
    const zoneParts = parts[zoneId] || [];
    return zoneParts.length > 0;
  }, [parts]);

  React.useEffect(() => {
    if (onCheckZoneHasParts) {
      onCheckZoneHasParts(checkIfZoneHasParts);
    }
  }, [checkIfZoneHasParts, onCheckZoneHasParts]);

  // Notify parent when zones with parts change (for auto-selecting zones on load).
  // Only when the list actually changes: the parent stores it in state, so a
  // fresh array on every `parts` change re-renders the whole request page —
  // during a drag that is once per pointer move, and every one of those re-renders
  // makes dnd-kit re-measure the drop targets.
  React.useEffect(() => {
    if (onZonesWithPartsChanged && !isLoading) {
      const zonesWithParts = Object.keys(parts).filter(zoneId => {
        const zoneParts = parts[zoneId] || [];
        return zoneParts.length > 0;
      });
      const previous = lastZonesWithPartsRef.current;
      const unchanged = previous
        && previous.length === zonesWithParts.length
        && previous.every((zoneId, i) => zoneId === zonesWithParts[i]);
      if (unchanged) return;
      lastZonesWithPartsRef.current = zonesWithParts;
      onZonesWithPartsChanged(zonesWithParts);
    }
  }, [parts, isLoading, onZonesWithPartsChanged]);

  const addPart = (zoneId, partName, lineAllowanceId = null) => {
    if (!partName.trim()) return;
    
    const newPartId = `${zoneId}-${Date.now()}`;
    const newPart = { 
      id: newPartId, 
      part: partName.trim(), 
      description: '',
      approach: 'replace',
      removeRefit: true,
      paint: true,
      panelbeating: true,
      cutAndWeld: false,
      paintScope: 'outer',
      paintSize: 'standard',
      forAccess: false,
      parent: null
    };
    if (lineAllowanceId) {
      newPart.lineAllowanceId = lineAllowanceId;
      // Default to innerOuter when InOut paint data is available and approach is replace
      if (hasInOutPaint(lineAllowanceId)) {
        newPart.paintScope = 'innerOuter';
      }
    }
    setParts(prev => ({
      ...prev,
      [zoneId]: [...(prev[zoneId] || []), newPart]
    }));

    // Auto-add default access parts since new parts default to R&R checked
    // and default child replacement parts since new parts default to replace
    if (lineAllowanceId) {
      addDefaultAccessParts(zoneId, newPartId, lineAllowanceId);
      addDefaultChildParts(zoneId, newPartId, lineAllowanceId);
    }
  };

  const addAccessPart = (zoneId, parentPart, partName) => {
    if (!partName.trim()) return;
    
    const newPartId = `${zoneId}-access-${Date.now()}`;
    setParts(prev => ({
      ...prev,
      [zoneId]: [
        ...(prev[zoneId] || []),
        {
          id: newPartId,
          part: partName.trim(),
          description: '',
          approach: 'remove-refit',
          removeRefit: true,
          paint: false,
          panelbeating: false,
          forAccess: true,
          parent: parentPart.id,
          images: [],
          source: 'manual'
        }
      ]
    }));
  };

  const addChildPart = (zoneId, parentPart, partName) => {
    if (!partName.trim()) return;
    
    const newPartId = `${zoneId}-child-${Date.now()}`;
    setParts(prev => ({
      ...prev,
      [zoneId]: [
        ...(prev[zoneId] || []),
        {
          id: newPartId,
          part: partName.trim(),
          description: '',
          approach: 'replace',
          removeRefit: true,
          paint: true,
          panelbeating: false,
          isChild: true,
          forAccess: false,
          parent: parentPart.id,
          images: [],
          source: 'manual'
        }
      ]
    }));
  };

  const addBlendPart = (zoneId, parentPart, partName) => {
    if (!partName.trim()) return;
    
    const newPartId = `${zoneId}-blend-${Date.now()}`;
    setParts(prev => ({
      ...prev,
      [zoneId]: [
        ...(prev[zoneId] || []),
        {
          id: newPartId,
          part: partName.trim(),
          description: '',
          approach: 'blend',
          removeRefit: false,
          paint: true,
          panelbeating: false,
          forBlend: true,
          parent: parentPart.id,
          images: [],
          source: 'manual'
        }
      ]
    }));
  };


  const updatePart = (zoneId, partId, field, value) => {
    setParts(prev => {
      let zoneParts = prev[zoneId].map(part => {
        if (part.id !== partId) return part;
        const updated = { ...part, [field]: value };
        // Switching to a different insurer line replaces the part's pricing basis.
        // Drop any manual hour/cost overrides that belonged to the old line so the
        // new line's allowances take effect, and reset paint scope/size to the new
        // line's defaults (mirrors a freshly added part).
        if (field === 'lineAllowanceId' && value && value !== part.lineAllowanceId) {
          updated.repairHoursOverride = null;
          updated.removeRefitHoursOverride = null;
          updated.paintCostOverride = null;
          updated.paintScope = hasInOutPaint(value) ? 'innerOuter' : 'outer';
          updated.paintSize = 'standard';
        }
        // If switching to replace, turn off panelbeating
        if (field === 'approach' && value === 'replace') {
          updated.panelbeating = false;
          // Default to innerOuter when InOut data is available
          if (hasInOutPaint(part.lineAllowanceId)) {
            updated.paintScope = 'innerOuter';
          }
        }
        // If switching to repair, turn on panelbeating and default paint to outer
        if (field === 'approach' && value === 'repair' && !part.panelbeating) {
          updated.panelbeating = true;
        }
        if (field === 'approach' && value === 'repair') {
          updated.paintScope = 'outer';
        }
        // When paint is turned off, reset paint scope and size
        if (field === 'paint' && !value) {
          updated.paintScope = 'outer';
          updated.paintSize = 'standard';
        }
        // Outer and In & Out can offer different sizes; drop a size the new scope
        // no longer offers rather than silently pricing off a missing column.
        if (field === 'paintScope') {
          const available = getPaintSizeOptions(part.lineAllowanceId, value).map(o => o.value);
          if (!available.includes(updated.paintSize)) {
            updated.paintSize = 'standard';
          }
        }
        return updated;
      });
      // When R&R is turned off, remove all child access parts
      if (field === 'removeRefit' && !value) {
        zoneParts = zoneParts.filter(p => !(p.forAccess && p.parent === partId));
      }
      // When approach changes from replace, remove all child replacement parts
      if (field === 'approach' && value !== 'replace') {
        zoneParts = zoneParts.filter(p => !(p.isChild && p.parent === partId));
      }
      // When paint is turned off, remove all child blend parts
      if (field === 'paint' && !value) {
        zoneParts = zoneParts.filter(p => !(p.forBlend && p.parent === partId));
      }
      return { ...prev, [zoneId]: zoneParts };
    });

    // When R&R is turned on, auto-add default access parts if the part has a lineAllowanceId
    if (field === 'removeRefit' && value) {
      const part = (parts[zoneId] || []).find(p => p.id === partId);
      if (part && part.lineAllowanceId && !part.forAccess) {
        // Only add if the part doesn't already have access parts
        const existingAccessParts = (parts[zoneId] || []).filter(p => p.forAccess && p.parent === partId);
        if (existingAccessParts.length === 0) {
          addDefaultAccessParts(zoneId, partId, part.lineAllowanceId);
        }
      }
    }

    // When approach changes to replace, auto-add default child replacement parts
    if (field === 'approach' && value === 'replace') {
      const part = (parts[zoneId] || []).find(p => p.id === partId);
      if (part && part.lineAllowanceId && !part.forAccess && !part.isChild) {
        const existingChildParts = (parts[zoneId] || []).filter(p => p.isChild && p.parent === partId);
        if (existingChildParts.length === 0) {
          addDefaultChildParts(zoneId, partId, part.lineAllowanceId);
        }
      }
    }

    // When paint is turned on, auto-add default blend parts
    if (field === 'paint' && value) {
      const part = (parts[zoneId] || []).find(p => p.id === partId);
      if (part && part.lineAllowanceId && !part.forAccess && !part.isChild && !part.forBlend) {
        const existingBlendParts = (parts[zoneId] || []).filter(p => p.forBlend && p.parent === partId);
        if (existingBlendParts.length === 0) {
          addDefaultBlendParts(zoneId, partId, part.lineAllowanceId);
        }
      }
    }
  };

  const removePart = (zoneId, partId) => {
    setParts(prev => {
      const zoneParts = prev[zoneId] || [];
      // Also remove child access and child replacement parts when removing a primary part
      const removedPart = zoneParts.find(p => p.id === partId);
      const idsToRemove = new Set([partId]);
      if (removedPart && !removedPart.forAccess && !removedPart.isChild && !removedPart.forBlend) {
        zoneParts.filter(p => (p.forAccess || p.isChild || p.forBlend) && p.parent === partId).forEach(p => idsToRemove.add(p.id));
      }
      return {
        ...prev,
        [zoneId]: zoneParts.filter(part => !idsToRemove.has(part.id))
      };
    });
  };

  // ── Drag-and-drop reordering ───────────────────────────────────────────
  // Primary parts can be reordered within a zone or dragged into another zone.
  // Child access/blend/replacement parts travel with their parent as one block.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const isChildPart = (p) => p.forAccess || p.isChild || p.forBlend;
  const primariesOf = (arr = []) => arr.filter(p => !isChildPart(p));
  const childrenOf = (arr = [], parentId) => arr.filter(p => isChildPart(p) && p.parent === parentId);

  // Rebuild a zone array as contiguous [primary, ...its children] blocks in the
  // given primary order. `childSource` supplies each primary's children and may
  // span both the origin and target zones during a cross-zone move.
  const regroupZone = (primaryOrder, childSource) => {
    const out = [];
    for (const prim of primaryOrder) {
      out.push(prim, ...childrenOf(childSource, prim.id));
    }
    return out;
  };

  // Which zone currently holds the given primary part id
  const findZoneOfPrimary = (partsState, primaryId) =>
    Object.keys(partsState).find(zoneId =>
      (partsState[zoneId] || []).some(p => p.id === primaryId && !isChildPart(p))
    ) || null;

  // Resolve a droppable/over id to a zone id (zone containers are "zone:<id>")
  const resolveZone = (partsState, overId) => {
    if (typeof overId === 'string' && overId.startsWith('zone:')) return overId.slice(5);
    return findZoneOfPrimary(partsState, overId);
  };

  // When a part crosses the vehicle's centre line, re-side it: swap to the
  // mirrored insurer line item when one exists, otherwise flip its name tokens.
  const applySideSwap = (partsState, primaryId, originZone, finalZone) => {
    const originSide = getSideFromZone(originZone);
    const finalSide = getSideFromZone(finalZone);
    if (originSide === finalSide) return partsState;
    if (finalSide !== 'left' && finalSide !== 'right') return partsState;

    const zoneArr = partsState[finalZone] || [];
    const idx = zoneArr.findIndex(p => p.id === primaryId);
    if (idx === -1) return partsState;

    const part = zoneArr[idx];
    const cache = insurerCacheRef.current;
    const mirror = cache ? findMirrorLineAllowance(part.part, cache.values(), finalSide) : null;

    let updated;
    if (mirror) {
      updated = { ...part, part: mirror.lineName, lineAllowanceId: mirror.$id };
    } else {
      const flipped = flipNameSide(part.part, finalSide);
      if (flipped === part.part) return partsState; // nothing to change
      updated = { ...part, part: flipped };
    }

    const newArr = [...zoneArr];
    newArr[idx] = updated;
    return { ...partsState, [finalZone]: newArr };
  };

  // Move the dragged part (and the child block that travels with it) into the
  // zone that `overId` belongs to. Returns `partsState` untouched when the part
  // is already there, so callers can test the result by identity.
  const moveToZoneOf = (partsState, activeId, overId, translatedTop, overRect) => {
    const fromZone = findZoneOfPrimary(partsState, activeId);
    const toZone = resolveZone(partsState, overId);
    if (!fromZone || !toZone || fromZone === toZone) return partsState;

    const fromArr = partsState[fromZone] || [];
    const toArr = partsState[toZone] || [];
    const fromPrims = primariesOf(fromArr);
    const toPrims = primariesOf(toArr);
    const activePrim = fromPrims.find(p => p.id === activeId);
    if (!activePrim) return partsState;

    // Insertion point among the target zone's primaries
    let newIndex;
    if (typeof overId === 'string' && overId.startsWith('zone:')) {
      newIndex = toPrims.length;
    } else {
      const overIndex = toPrims.findIndex(p => p.id === overId);
      const isBelow = translatedTop != null && overRect && translatedTop > overRect.top + overRect.height / 2;
      newIndex = overIndex >= 0 ? overIndex + (isBelow ? 1 : 0) : toPrims.length;
    }

    const newFromPrims = fromPrims.filter(p => p.id !== activeId);
    const newToPrims = [...toPrims.slice(0, newIndex), activePrim, ...toPrims.slice(newIndex)];
    const childSource = [...fromArr, ...toArr]; // active's children still live in fromArr

    return {
      ...partsState,
      [fromZone]: regroupZone(newFromPrims, fromArr),
      [toZone]: regroupZone(newToPrims, childSource),
    };
  };

  const handleDragStart = (event) => {
    const id = event.active.id;
    setActiveDragId(id);
    dragOriginZoneRef.current = findZoneOfPrimary(parts, id);
    lastMoveDeltaRef.current = null;
  };

  const handleDragCancel = () => {
    setActiveDragId(null);
    lastMoveDeltaRef.current = null;
  };

  // Live cross-zone relocation so the dragged part joins the target zone's
  // SortableContext (which then drives the intra-zone placeholder). Same-zone
  // moves are left to the SortableContext and finalised in handleDragEnd.
  //
  // At most one relocation per cursor position, which is what stops the page
  // from crashing mid-drag. dnd-kit fires onDragOver from an effect keyed on the
  // id under the cursor, and it re-runs collision detection against freshly
  // measured rects on every render. Moving a part rewrites those rects — most
  // violently when it was the last part in its section, which collapses from a
  // full row to the one-line "Drag parts here" placeholder and drags every
  // section below it up the page. That can put a different id under a cursor
  // that has not moved, so the effect fires again, moves the part back, restores
  // the old layout, and fires again. The whole ping-pong is scheduled from
  // effects with no pointer event between the links, so React counts it as one
  // runaway update and throws "Maximum update depth exceeded" (#185), blanking
  // the page before the assessor has even released the mouse. The cursor offset
  // only changes when the pointer really moves, so keying off it lets a genuine
  // drag move the part as often as the assessor likes while a layout echo gets
  // exactly one turn.
  const handleDragOver = (event) => {
    const { active, over, delta } = event;
    if (!over) return;
    const previousDelta = lastMoveDeltaRef.current;
    if (previousDelta && delta && previousDelta.x === delta.x && previousDelta.y === delta.y) return;
    lastMoveDeltaRef.current = delta;

    const activeId = active.id;
    const overId = over.id;
    const translatedTop = active.rect?.current?.translated?.top;
    const overRect = over.rect;

    setParts(prev => moveToZoneOf(prev, activeId, overId, translatedTop, overRect));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragId(null);
    const originZone = dragOriginZoneRef.current;
    dragOriginZoneRef.current = null;
    lastMoveDeltaRef.current = null;
    if (!over) return;
    const activeId = active.id;
    const overId = over.id;
    const translatedTop = active.rect?.current?.translated?.top;
    const overRect = over.rect;

    setParts(prev => {
      // A cross-zone move the drag-over handler declined — the cursor landed on
      // the new section without moving again — is finalised here, so the drop
      // always lands in the section it was released over.
      let next = moveToZoneOf(prev, activeId, overId, translatedTop, overRect);
      const relocated = next !== prev;

      const activeZone = findZoneOfPrimary(next, activeId);
      if (!activeZone) return next;
      const toZone = resolveZone(next, overId) || activeZone;

      // Finalise ordering within the target zone using arrayMove semantics so
      // the drop lands exactly where the placeholder showed (both directions).
      // A relocation above already inserted the part at the right index.
      if (!relocated && activeZone === toZone && overId !== activeId) {
        const arr = next[activeZone] || [];
        const prims = primariesOf(arr);
        const oldIndex = prims.findIndex(p => p.id === activeId);
        const newIndex = prims.findIndex(p => p.id === overId);
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          next = { ...next, [activeZone]: regroupZone(arrayMove(prims, oldIndex, newIndex), arr) };
        }
      }
      // Re-side the part if the drag crossed the vehicle's centre line
      next = applySideSwap(next, activeId, originZone, toZone);
      return next;
    });
  };

  const handleRemoveZone = (zoneId) => {
    const zoneParts = parts[zoneId] || [];
    if (zoneParts.length > 0) {
      setConfirmDelete(zoneId);
    } else {
      onRemoveZone(zoneId);
    }
  };

  const confirmRemoveZone = () => {
    if (confirmDelete) {
      setParts(prev => {
        const updated = { ...prev };
        delete updated[confirmDelete];
        return updated;
      });
      onRemoveZone(confirmDelete);
      setConfirmDelete(null);
    }
  };

  // Use a ref to hold the pending lineAllowanceId — set synchronously by onPartSelected
  // before onChange fires, avoiding React batched state update race condition
  const pendingLineAllowanceId = useRef({});

  const handleAddPart = (zoneId, partName) => {
    if (partName && partName.trim()) {
      const lineAllowanceId = pendingLineAllowanceId.current[zoneId] || null;
      addPart(zoneId, partName, lineAllowanceId);
      // Clear the add part input and pending lineAllowanceId for this zone
      setAddPartValues(prev => ({ ...prev, [zoneId]: '' }));
      pendingLineAllowanceId.current[zoneId] = null;
    }
  };

  const openImageSelector = (zoneId, partId, partName) => {
    setImageSelector({ open: true, zoneId, partId, partName });
  };

  const closeImageSelector = () => {
    setImageSelector({ open: false, zoneId: null, partId: null, partName: '' });
  };

  const toggleImageForPart = async (imageId) => {
    const { zoneId, partId, partName } = imageSelector;
    if (!zoneId) return;

    // Zone-level image assignment (no partId — clicked from zone gallery + button)
    if (!partId) {
      const image = availableImages.find(img => (img.id || img.$id) === imageId);
      if (!image) return;

      // Toggle zone: if already in this zone, clear it; otherwise assign
      const currentZone = image.zone || '';
      const newZone = currentZone === zoneId ? '' : zoneId;

      try {
        await dataApi.updateQuoteRequestImage(imageId, { zone: newZone });
        console.log('[DamagePartsList] Updated image zone:', { imageId, zone: newZone });
      } catch (err) {
        console.error('[DamagePartsList] Failed to update image zone:', err);
      }
      return;
    }

    // Part-level image tagging (has partId)
    // Find the part to get its name
    const currentPart = parts[zoneId]?.find(p => p.id === partId);
    if (!currentPart) return;

    const effectivePartName = partName || currentPart.part;

    // Get the image to read its current tags
    const image = availableImages.find(img => img.id === imageId || img.$id === imageId);
    if (!image) {
      console.warn('[DamagePartsList] Image not found:', imageId);
      return;
    }

    // Parse existing tags
    let existingTags = [];
    try {
      existingTags = JSON.parse(image.tags || '[]');
      if (!Array.isArray(existingTags)) {
        existingTags = [];
      }
    } catch (err) {
      console.warn('[DamagePartsList] Failed to parse image tags:', err);
      existingTags = [];
    }

    // Normalize tags to objects with name and confidence
    const normalizedTags = existingTags.map(tag => {
      if (typeof tag === 'string') {
        return { name: tag, confidence: 100 };
      }
      return tag;
    });

    // Check if tag already exists
    const hasPartTag = normalizedTags.some(tag => 
      (tag.name || '').toLowerCase().trim() === effectivePartName.toLowerCase().trim()
    );

    let updatedTags;
    if (hasPartTag) {
      // Removing tag
      updatedTags = normalizedTags.filter(tag => 
        (tag.name || '').toLowerCase().trim() !== effectivePartName.toLowerCase().trim()
      );
    } else {
      // Adding tag
      updatedTags = [...normalizedTags, { name: effectivePartName, confidence: 100 }];
    }

    // Update the image in the database - realtime will handle UI update
    try {
      await dataApi.updateQuoteRequestImage(imageId, {
        tags: JSON.stringify(updatedTags)
      });

      console.log('[DamagePartsList] Updated image tags:', {
        imageId,
        partName: effectivePartName,
        action: hasPartTag ? 'removed' : 'added',
        tagsCount: updatedTags.length
      });
    } catch (err) {
      console.error('[DamagePartsList] Failed to update image tags:', err);
    }
  };

  const handleUploadImage = () => {
    // Trigger file input click
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        // TODO: Upload files to server and get image IDs
        console.log('Files selected for upload:', files);
        // For now, just show a message
        // In a real implementation, you would upload to Appwrite storage
      }
    };
    input.click();
  };

  // Get all images for a part based ONLY on tags (single source of truth)
  const getImagesForPart = (part) => {
    const partName = (part.part || '').toLowerCase().trim();
    
    if (!partName) {
      return [];
    }
    
    // Find images with matching tags
    return availableImages.filter(img => {
      try {
        const tags = JSON.parse(img.tags || '[]');
        if (!Array.isArray(tags)) return false;
        
        return tags.some(tag => {
          const tagName = typeof tag === 'string' ? tag : (tag.name || '');
          return tagName.toLowerCase().trim() === partName;
        });
      } catch (err) {
        return false;
      }
    });
  };

  // Card title for a zone not in the ZONES list (e.g. interior, tray) — title-cased
  // zone id, or "Other" when a section has no id.
  const sectionLabel = (zoneId) => {
    if (!zoneId) return 'Other';
    const known = ZONES.find((z) => z.id === zoneId);
    if (known) return known.label;
    return zoneId.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // A section's image gallery — shared by the All view and the grouped views so
  // every view shows the same photos with the same styling.
  const renderZoneGallery = (zoneId, zoneLabelText) => {
    const zoneImages = availableImages.filter(img => img.zone === zoneId);
    return (
      <div className="pb-2 border-b border-slate-100">
        <div className="flex gap-2 overflow-x-auto py-1 items-center" style={{ scrollbarWidth: 'thin' }}>
          {zoneImages.map(image => {
            const imageId = image.id || image.$id;
            return (
              <div key={imageId} className="flex-shrink-0 w-20 h-20 relative group/img">
                <img
                  src={image.url}
                  alt={image.name || 'Zone image'}
                  className="w-full h-full object-cover rounded border border-slate-200 cursor-pointer hover:border-primary transition-colors"
                  onClick={() => onImageClick && onImageClick(imageId, zoneImages)}
                />
              </div>
            );
          })}
          {zoneImages.length === 0 && (
            <span className="text-xs text-muted-foreground py-2">No images for this section</span>
          )}
          <button
            type="button"
            onClick={() => openImageSelector(zoneId, null, zoneLabelText)}
            className="flex-shrink-0 w-20 h-20 border-2 border-dashed border-border rounded flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  };

  // Directly set a field on one part.
  const setPartFieldById = (partId, patch) => {
    setParts((prev) => {
      const next = {};
      Object.keys(prev).forEach((z) => {
        next[z] = (prev[z] || []).map((p) => (p.id === partId ? { ...p, ...patch } : p));
      });
      return next;
    });
  };

  // Remove a single child part (access / blend / replacement) by id.
  const deletePartById = (partId) => {
    setParts((prev) => {
      const next = {};
      Object.keys(prev).forEach((z) => {
        next[z] = (prev[z] || []).filter((p) => p.id !== partId);
      });
      return next;
    });
  };

  // Turn off an operation on a primary part and drop the children that operation
  // owns (R&R off -> its access parts; replace off -> its replacement children).
  const turnOffOperation = (partId, patch, ownsChild) => {
    setParts((prev) => {
      const next = {};
      Object.keys(prev).forEach((z) => {
        next[z] = (prev[z] || [])
          .map((p) => (p.id === partId ? { ...p, ...patch } : p))
          .filter((p) => !(p.parent === partId && ownsChild(p)));
      });
      return next;
    });
  };

  // The R&R / Blend / Parts views: a compact list of just those items, grouped by
  // section and honouring the zone filter. Each item deletes the way that makes
  // sense — a primary operation is turned off (its children go with it); a child
  // item is simply removed.
  const renderGroupedView = (kind) => {
    const config = {
      rr: {
        empty: 'No R&R items',
        matches: (p) => (!isChildPart(p) && p.removeRefit) || p.forAccess,
        entryFor: (p) =>
          p.forAccess
            ? { subtitle: 'For access', onDelete: () => deletePartById(p.id) }
            : { onDelete: () => turnOffOperation(p.id, { removeRefit: false }, (c) => c.forAccess) },
      },
      blend: {
        empty: 'No blend items',
        matches: (p) => p.forBlend || (!isChildPart(p) && p.paint && p.blend),
        entryFor: (p) =>
          p.forBlend
            ? { onDelete: () => deletePartById(p.id) }
            : { onDelete: () => setPartFieldById(p.id, { blend: false }) },
      },
      parts: {
        empty: 'No replacement items',
        matches: (p) => (!isChildPart(p) && p.approach === 'replace') || p.isChild,
        entryFor: (p) =>
          p.isChild
            ? { onDelete: () => deletePartById(p.id) }
            : { onDelete: () => turnOffOperation(p.id, { approach: 'repair' }, (c) => c.isChild) },
      },
    }[kind];

    // Collect matching parts into their section (the zone key they live under).
    const byZone = {};
    Object.keys(parts).forEach((zoneId) => {
      if (filterZone && filterZone !== zoneId) return; // honour the zone filter
      (parts[zoneId] || []).forEach((p) => {
        if (!config.matches(p)) return;
        const z = zoneId || 'other';
        if (!byZone[z]) byZone[z] = [];
        byZone[z].push({ part: p, zoneKey: zoneId, ...config.entryFor(p) });
      });
    });

    const zones = Object.keys(byZone).sort((a, b) => zoneRank(a) - zoneRank(b));
    const total = zones.reduce((n, z) => n + byZone[z].length, 0);

    if (total === 0) {
      return (
        <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
          {config.empty}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {zones.map((zoneId) => (
          <Card key={zoneId}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{sectionLabel(zoneId)}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {renderZoneGallery(zoneId, sectionLabel(zoneId))}
              {/* Compact rows — the left spacer matches the All view's drag handle so
                  the name field sits at the same x, but there is no drag here. */}
              <div>
                {byZone[zoneId].map(({ part, zoneKey, subtitle, onDelete }) => (
                  <div key={part.id} className="group/item flex items-center">
                    <DragHandleSpacer />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 p-2 pr-0.5">
                        <div className="w-160 flex-shrink-0">
                          <PartLookupInput
                            value={part.part}
                            onChange={(name) => updatePart(zoneKey, part.id, 'part', name)}
                            onPartSelected={isChildPart(part) ? undefined : (sel) => {
                              if (sel && sel.$id) updatePart(zoneKey, part.id, 'lineAllowanceId', sel.$id);
                            }}
                            insurerConfigId={insurerConfigId}
                            zone={zoneKey}
                            placeholder="Search or enter part name"
                            className="mb-0"
                            onFocus={() => trackFieldEdit(`part_${part.id}`)}
                            onBlur={clearFieldEdit}
                            editor={getFieldEditor(`part_${part.id}`)}
                            isCurrentUser={isCurrentUserEditing(`part_${part.id}`)}
                          />
                        </div>
                        {subtitle && (
                          <span className="text-xs text-muted-foreground flex-shrink-0">{subtitle}</span>
                        )}
                        <QuantityField
                          value={part.quantity}
                          onChange={(n) => updatePart(zoneKey, part.id, 'quantity', n)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={onDelete}
                          title={`Remove ${part.part}`}
                          className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 flex-shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  // One primary part's row for the "All" view — name lookup, Approach/R&R/Paint
  // buttons (which carry the nested access/blend/replacement children), description
  // and delete.
  const renderPartRow = (part, zoneId, zoneParts) => {
    const childAccessParts = zoneParts.filter((p) => p.forAccess && p.parent === part.id)
      .map((ap) => ({ ...ap, ...getPartDefaults(ap) }));
    const childReplacementParts = zoneParts.filter((p) => p.isChild && p.parent === part.id);
    const childBlendParts = zoneParts.filter((p) => p.forBlend && p.parent === part.id);
    const partWithDefaults = { ...part, ...getPartDefaults(part) };

    return (
      <div className="flex items-center gap-2 p-2 pr-0.5">
        {/* Part name */}
        <div className="w-160 flex-shrink-0">
          <PartLookupInput
            value={part.part}
            onChange={(partName) => updatePart(zoneId, part.id, 'part', partName)}
            onPartSelected={(selectedPart) => {
              if (selectedPart && selectedPart.$id) {
                updatePart(zoneId, part.id, 'lineAllowanceId', selectedPart.$id);
              }
            }}
            insurerConfigId={insurerConfigId}
            zone={zoneId}
            placeholder="Search or enter part name"
            className="mb-0"
            onFocus={() => trackFieldEdit(`part_${part.id}`)}
            onBlur={clearFieldEdit}
            editor={getFieldEditor(`part_${part.id}`)}
            isCurrentUser={isCurrentUserEditing(`part_${part.id}`)}
          />
        </div>

        {/* Action buttons */}
        <div className="flex gap-1 flex-shrink-0">
          <ApproachButton
            part={partWithDefaults}
            zoneId={zoneId}
            onUpdatePart={updatePart}
            childReplacementParts={childReplacementParts}
            onAddChildPart={addChildPart}
            onRemoveChildPart={removePart}
            onUpdateChildPart={updatePart}
            insurerConfigId={insurerConfigId}
            hasInOutPaint={hasInOutPaint}
            defaultsReady={insurerCacheReady}
          />
          <RemoveRefitButton
            part={partWithDefaults}
            zoneId={zoneId}
            onUpdatePart={updatePart}
            childAccessParts={childAccessParts}
            onAddAccessPart={addAccessPart}
            onRemoveAccessPart={removePart}
            onUpdateAccessPart={updatePart}
            insurerConfigId={insurerConfigId}
            defaultsReady={insurerCacheReady}
          />
          <PaintButton
            part={partWithDefaults}
            zoneId={zoneId}
            onUpdatePart={updatePart}
            childBlendParts={childBlendParts}
            onAddBlendPart={addBlendPart}
            onRemoveBlendPart={removePart}
            onUpdateBlendPart={updatePart}
            insurerConfigId={insurerConfigId}
            hasInOutPaint={hasInOutPaint}
            paintSizeOptions={getPaintSizeOptions(
              partWithDefaults.lineAllowanceId,
              partWithDefaults.paintScope || 'outer'
            )}
            blendUnitPrice={blendUnitPrice ?? null}
            defaultsReady={insurerCacheReady}
          />
        </div>

        {/* How many of this fitting the repair disturbs — see QuantityField */}
        <QuantityField
          value={part.quantity}
          onChange={(n) => updatePart(zoneId, part.id, 'quantity', n)}
        />

        {/* Description — fills remaining space */}
        <div className="flex-1 min-w-0">
          <PresenceInput
            value={part.description}
            onChange={(e) => updatePart(zoneId, part.id, 'description', e.target.value)}
            placeholder="Description..."
            className="h-8 text-sm"
            onFocus={() => trackFieldEdit(`description_${part.id}`)}
            onBlur={clearFieldEdit}
            editor={getFieldEditor(`description_${part.id}`)}
            isCurrentUser={isCurrentUserEditing(`description_${part.id}`)}
          />
        </div>

        {/* Delete — ghost, visible on hover */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => removePart(zoneId, part.id)}
          className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 flex-shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  // R&R / Blend / Parts render as compact, section-grouped item lists.
  if (viewMode === 'rr' || viewMode === 'blend' || viewMode === 'parts') {
    return renderGroupedView(viewMode);
  }

  // Nothing assessed yet. Rather than a one-line hint, give the section room and
  // put the two real starting points in front of the user: add a section by hand,
  // or let the AI draft one from the photos.
  if (selectedZones.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed py-14 px-6 text-center">
        <div className="space-y-1">
          <p className="text-sm font-medium">No damage assessed yet</p>
          <p className="text-sm text-muted-foreground max-w-md">
            Add a section and list the damaged parts yourself, or let the AI draft an assessment
            from the photos and edit it from there.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-center">
          {onAddSection && (
            <Button variant="outline" size="sm" onClick={onAddSection}>
              <Plus className="h-4 w-4 mr-2" />
              Add a section
            </Button>
          )}
          {onGenerateAssessment && (
            <Button
              size="sm"
              onClick={onGenerateAssessment}
              disabled={!canGenerateAssessment || assessmentRunning}
              title={canGenerateAssessment
                ? 'Run the AI damage assessment on the uploaded photos'
                : assessBlockedReason}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate draft assessment
            </Button>
          )}
        </div>
        {onGenerateAssessment && !canGenerateAssessment && (
          <p className="text-xs text-muted-foreground">{assessBlockedReason}.</p>
        )}
      </div>
    );
  }

  return (
    <>
      <DndContext
        sensors={dndSensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
      <div className="space-y-4">
        {selectedZones
          .filter(zoneId => !filterZone || filterZone === zoneId)
          .map(zoneId => {
          const zone = ZONES.find(z => z.id === zoneId);
          const zoneParts = parts[zoneId] || [];

          return (
            <Card key={zoneId}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{zone?.label || sectionLabel(zoneId)}</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveZone(zoneId)}
                    className="h-8 w-8 p-0 text-slate-500 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Zone-level image gallery */}
                {renderZoneGallery(zoneId, zone?.label)}

                {/* Primary parts — drag the left handle to reorder or move between sections */}
                {(() => {
                  const primaryParts = zoneParts.filter(p => !p.forAccess && !p.isChild && !p.forBlend);
                  return (
                    <Droppable id={`zone:${zoneId}`}>
                      <SortableContext items={primaryParts.map(p => p.id)} strategy={verticalListSortingStrategy}>
                        {primaryParts.length === 0 && (
                          <div className="flex items-center justify-center py-4 text-xs text-muted-foreground border-2 border-dashed border-slate-200 rounded-md">
                            Drag parts here
                          </div>
                        )}
                        {primaryParts.map(part => (
                          <SortableRow key={part.id} id={part.id} className="group/item">
                            {renderPartRow(part, zoneId, zoneParts)}
                          </SortableRow>
                        ))}
                      </SortableContext>
                    </Droppable>
                  );
                })()}

                {/* Lookup control for adding new part */}
                <div className="w-160 flex-shrink-0">
                  <PartLookupInput
                    value={addPartValues[zoneId] || ''}
                    onChange={(partName) => {
                      if (partName && partName.trim()) {
                        handleAddPart(zoneId, partName);
                      }
                    }}
                    onPartSelected={(selectedPart) => {
                      if (selectedPart && selectedPart.$id) {
                        pendingLineAllowanceId.current[zoneId] = selectedPart.$id;
                      }
                    }}
                    insurerConfigId={insurerConfigId}
                    zone={zoneId}
                    placeholder="Add a part..."
                    commitOnBlur={false}
                    clearOnChange={true}
                    className="mb-0"
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <DragOverlay dropAnimation={{ duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
        {activeDragId ? (() => {
          const draggedPart = Object.values(parts)
            .flat()
            .find(p => p.id === activeDragId);
          return (
            <div className="flex items-center gap-2 rounded-md bg-background shadow-lg ring-1 ring-black/5 px-2 py-2">
              <GripVertical className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <span className="text-sm font-medium truncate">
                {draggedPart?.part || 'Part'}
              </span>
            </div>
          );
        })() : null}
      </DragOverlay>
      </DndContext>

      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Section</DialogTitle>
            <DialogDescription>
              This section has parts assigned to it. Removing it will also delete all associated parts. Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmRemoveZone}>
              Remove Section & Parts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageSelector
        open={imageSelector.open}
        onOpenChange={closeImageSelector}
        availableImages={availableImages}
        partName={imageSelector.partName || 'Part'}
        selectedImageIds={
          imageSelector.partId
            ? (() => {
                const part = parts[imageSelector.zoneId]?.find(p => p.id === imageSelector.partId);
                if (!part) return [];
                // Get images based on tags only
                return getImagesForPart(part).map(img => img.id || img.$id);
              })()
            : availableImages
                .filter(img => img.zone === imageSelector.zoneId)
                .map(img => img.id || img.$id)
        }
        onToggleImage={toggleImageForPart}
        onUpload={handleUploadImage}
      />
    </>
  );
};

export default DamagePartsList;
